<template>
  <div>
    <!-- Loading state -->
    <div
      v-if="pending"
      class="flex flex-col items-center justify-center gap-4 py-24"
    >
      <div
        class="h-8 w-8 animate-spin rounded-full border-2 border-depth-3 border-t-brand"
      />
      <p class="text-mouse">
        {{ t('general/non_special_texts/loading') }}
      </p>
    </div>

    <!-- Not found (404) vs fetch error (network/bug) -->
    <div
      v-else-if="showFallback"
      class="bg-[url('/sc/images/svgs/landingpage_bg.svg')] bg-repeat bg-depth-0 flex items-center"
    >
      <div class="w-full max-w-screen-xl mx-auto px-4 pt-16 pb-24 text-center">
        <h1 class="text-2xl font-bold text-mouse uppercase">
          {{
            isNotFound
              ? t('general/error/alternative_title')
              : t('general/non_special_texts/error')
          }}
        </h1>
        <p class="mt-4 text-rhino">
          {{
            isNotFound
              ? t('general/error/alternative_subtitle')
              : t('general/error/text')
          }}
        </p>
        <div class="mt-8 flex flex-wrap justify-center gap-3">
          <common-button
            v-if="hasFetchError"
            size="lg"
            variant="primary"
            class="inline-flex items-center gap-2"
            @click="refresh()"
          >
            {{ t('general/retry') }}
          </common-button>
          <common-button
            size="lg"
            variant="primary"
            :to="'/' + safeLocaleCode"
            class="inline-flex items-center gap-2"
          >
            <fa-icon :icon="faArrowLeft" class="size-4" aria-hidden="true" />
            {{ t('frontend/back') }}
          </common-button>
        </div>
      </div>
    </div>

    <main
      v-else-if="strapiContent"
      data-testid="landing-page-wrapper"
      class="relative bg-[url('/sc/images/svgs/landingpage_bg.svg')] bg-repeat bg-depth-0"
    >
      <client-only>
        <lazy-landing-page-region-warning
          v-if="!!strapiContent.chargebeePlanId"
        />
      </client-only>

      <!-- Header cover image with gradient mask (fade to transparent at bottom) -->
      <div
        v-if="strapiContent.headerCoverImage"
        class="pointer-events-none absolute inset-0 w-full h-full overflow-hidden opacity-25"
      >
        <strapi-image-overlay
          auto-scale
          :lazy="false"
          :alt-fallback="`${strapiContent.title} Background`"
          :image="strapiContent.headerCoverImage"
          class="[&_img]:h-auto [&_img]:w-full [&_img]:max-w-full"
          style="
            mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
            -webkit-mask-image: linear-gradient(
              to bottom,
              black 70%,
              transparent 100%
            );
          "
        />
      </div>

      <div class="relative overflow-x-hidden">
        <landing-page-top-bar
          v-if="strapiTopBar.length > 0"
          :benefits="strapiTopBar"
        />

        <div class="mx-auto max-w-screen-xl px-4">
          <common-breadcrumb class="mt-4 lg:mt-6" :crumbs="breadCrumbs" />

          <strapi-product-header
            class="mb-16 mt-4 lg:mt-6"
            :pre-title="strapiContent.subHeadline"
            :default-order-url="strapiContent.orderUrl"
            :pre-order="isPreOrder"
            :product-key="strapiContent.productKey"
            :chargebee-plan-id="strapiContent.chargebeePlanId"
            :product-type="strapiContent.type"
            :platform="strapiContent.type"
            :starting="strapiContent.startConfiguration"
            :title="strapiContent.title"
            :microsoft-store="strapiContent.type === StrapiProductType.Xbox"
            :is-discounted-game="strapiContent.isDiscountedGame"
            :discounted-percentage="strapiContent.discountedPercentage"
          />

          <lazy-strapi-countdown
            v-if="strapiCountdown?.countdown"
            :component="strapiCountdown.countdown"
          />
        </div>

        <strapi-product-components :product="strapiContent" />
      </div>

      <common-scroll-to-top />
    </main>
  </div>
</template>

<script lang="ts" setup>
import { onUnmounted } from 'vue';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import {
  StrapiProductType,
  type GetProductQuery,
  type StrapiProduct,
  type StrapiProductCountdown,
  type StrapiProductFaq,
} from '~/apollo/types/types';
import type { BreadCrumb } from '../common/types';
import useSafeLocaleCode from '~/composables/useSafeLocaleCode';
import useLandingPageData from './composables/useLandingPageData';
import { trackViewItem } from '~/helpers/gaEcommerce';
import { useGoogleFaqPage } from '~/helpers/googleFaqPage';
import { useGoogleBreadcrumb } from '~/helpers/googleBreadcrumb';
import { serializeJsonLd } from '~/helpers/jsonLd';
import { useCurrencyStore } from '~/store/currency';

const props = defineProps<{
  slug: string;
}>();

const route = useRoute();
const { t, locale } = useI18n();
const safeLocaleCode = useSafeLocaleCode();

const slugRef = toRef(props, 'slug');
const {
  refresh,
  landingPageContent,
  data,
  pending,
  isNotFound,
  isError: hasFetchError,
} = await useLandingPageData(slugRef);

const showFallback = computed(
  () =>
    !pending.value &&
    (isNotFound.value ||
      hasFetchError.value ||
      data.value?.strapiContent == null),
);

// set SEO
const localizedSlugs = computed(() => landingPageContent.value?.localizedSlugs);
useI18nLocalizedSlugs(localizedSlugs);

const googleProduct = computed(() => data.value.googleProduct);
const config = useRuntimeConfig();

const googleFaqPage = computed(() => {
  const faqComponent = data.value.strapiContent?.content?.find(
    (c) => c.component === 'product.faqs',
  ) as StrapiProductFaq | undefined;

  return useGoogleFaqPage(faqComponent?.frequentlyAskedQuestions);
});

const googleBreadcrumb = computed(() =>
  useGoogleBreadcrumb(
    config.public.websiteUrl,
    locale.value,
    t('app/home'),
    data.value.breadCrumbs,
    route.path,
  ),
);

useHead({
  title: landingPageContent.value?.metaTitle,
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: '/sc/images/svgs/landingpage_bg.svg',
    },
  ],
  script: () => {
    const scripts = [];
    if (googleProduct.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: googleProduct.value,
      });
    }
    if (googleFaqPage.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleFaqPage.value),
      });
    }
    if (googleBreadcrumb.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleBreadcrumb.value),
      });
    }
    return scripts;
  },
});

useSeoMeta({
  title: () => landingPageContent.value?.metaTitle,
  description: () => landingPageContent.value?.metaDescription,
  ogTitle: () => landingPageContent.value?.metaTitle,
  ogImage: () => landingPageContent.value?.headerCoverImage?.url,
  ogDescription: () => landingPageContent.value?.metaDescription,
  ogUrl: () => `${config.public.websiteUrl}/${route.fullPath}`,
  twitterImage: () => landingPageContent.value?.headerCoverImage?.url,
  twitterTitle: () => landingPageContent.value?.metaTitle,
  twitterDescription: () => landingPageContent.value?.metaDescription,
});

const { scrollToTop } = useWindowScroll();

// watch currency change -> re-fetch content
useUserCurrency(refresh, async () => {
  await refresh();
  scrollToTop();
});

// watch for region changes -> re-fetch content
const authStore = useAuthStore();
const regionWatchHandle = watch(
  () => authStore.activeRegion,
  async () => {
    await refresh();
    scrollToTop();
  },
);

onUnmounted(() => {
  regionWatchHandle();
});

const breadCrumbs = computed<Array<BreadCrumb>>(() => data.value.breadCrumbs);
const strapiContent = computed<StrapiProduct>(() => data.value.strapiContent);
const strapiTopBar = computed<GetProductQuery['product_top_bar']>(() => {
  // render product specific top bar
  if (
    strapiContent.value != null &&
    strapiContent.value.topBarFeature != null
  ) {
    return [strapiContent.value.topBarFeature];
  }

  return data.value.strapiTopBar;
});
const strapiCountdown = computed<StrapiProductCountdown>(
  () => data.value.strapiCountdown as StrapiProductCountdown,
);

const isPreOrder = computed<boolean>(() =>
  timeIsAfterNow(data.value.strapiContent?.releaseAt),
);

// GA4 view_item — fire once per (product, currency) on the client.
// Re-fires after a currency switch (refresh()) so the price tracked matches
// what the user actually sees.
const currencyStore = useCurrencyStore();
const trackedViewItemKey = ref<string | null>(null);
watch(
  () => {
    const sc = data.value.strapiContent;
    if (!sc?.productKey) return null;
    const priceEntry = sc.startConfiguration?.prices?.find(
      (p) => p.currency === currencyStore.currency,
    );
    // Include the price in the dedupe key so we re-fire once prices finish
    // loading (initial render can run with prices still empty) and on any
    // subsequent price change for the same (product, currency).
    return `${sc.productKey}__${currencyStore.currency}__${priceEntry?.value ?? 'no-price'}`;
  },
  (key) => {
    if (!import.meta.client || !key || key === trackedViewItemKey.value) return;
    const sc = data.value.strapiContent;
    if (!sc?.productKey) return;
    const priceEntry = sc.startConfiguration?.prices?.find(
      (p) => p.currency === currencyStore.currency,
    );
    const fraction = priceEntry?.currency === 'JPY' ? 0 : 2;
    const price = priceEntry ? priceEntry.value / 10 ** fraction : undefined;
    trackViewItem({
      productKey: sc.productKey,
      // StrapiProduct.name is frequently empty (GA4 reports item_name as
      // "(not set)"); fall back to the display title, matching the name the
      // checkout funnel events send. `||` so an empty string falls through.
      name: sc.name || sc.title,
      category: sc.type,
      price,
      currency: priceEntry?.currency ?? currencyStore.currency,
    });
    trackedViewItemKey.value = key;
  },
  { immediate: true },
);
</script>
